// src/utils/fieldCompatibility.ts
// Helper functions to safely access fields with legacy fallbacks

import { League, Team, Player, PlayerStats, ScoringSystem } from '../types';

// Safe accessors for League
export const getLeagueField = {
  type: (league: League): string => league.leagueType || league.league_type || 'season_long',
  maxTeams: (league: League): number => league.maxTeams || league.max_teams || 10,
  entryFee: (league: League): number => league.entryFee || league.entry_fee || 0,
  status: (league: League): string => league.leagueStatus || league.league_status || 'setup',
  createdAt: (league: League): string => league.createdAt || league.created_at || new Date().toISOString(),
  commissionerId: (league: League): number => league.commissionerId || league.commissioner_id || 0,
};

// Safe accessors for Team
export const getTeamField = {
  name: (team: Team): string => team.teamName || team.team_name || team.name || 'Team',
  pointsFor: (team: Team): number => team.pointsFor || team.points_for || 0,
  pointsAgainst: (team: Team): number => team.pointsAgainst || team.points_against || 0,
  leagueId: (team: Team): number => team.leagueId || team.league_id || 0,
  userId: (team: Team): number => team.userId || team.user_id || 0,
  createdAt: (team: Team): string => team.createdAt || team.created_at || new Date().toISOString(),
};

// Safe accessors for Player
export const getPlayerField = {
  jerseyNumber: (player: Player): number => player.jerseyNumber || player.jersey_number || 0,
  experience: (player: Player): number => player.experienceYears || player.experience || 0,
  isActive: (player: Player): boolean => player.isActive ?? player.is_active ?? true,
  nflTeam: (player: Player): any => player.nflTeam || player.nfl_team,
  nflTeamId: (player: Player): number => player.nflTeamId || player.nfl_team_id || 0,
  fantasyPointsAvg: (player: Player): number => player.fantasy_points_avg || 0,
};

// Safe accessors for PlayerStats
export const getStatsField = {
  playerId: (stats: PlayerStats): number => stats.playerId || stats.player_id || 0,
  fantasyPoints: (stats: PlayerStats): number => stats.fantasyPoints || stats.fantasy_points || 0,
  fgMade: (stats: PlayerStats): number => stats.fgMade || (stats.fg_made_0_39 || 0) + (stats.fg_made_40_49 || 0) + (stats.fg_made_50_plus || 0) || 0,
  fgAttempts: (stats: PlayerStats): number => stats.fgAttempts || (stats.fg_att_0_39 || 0) + (stats.fg_att_40_49 || 0) + (stats.fg_att_50_plus || 0) || 0,
  fgMadeUnder40: (stats: PlayerStats): number => stats.fgMadeUnder40 || stats.fg_made_0_39 || 0,
  fgMade4049: (stats: PlayerStats): number => stats.fgMade4049 || stats.fg_made_40_49 || 0,
  fgMade50Plus: (stats: PlayerStats): number => stats.fgMade50Plus || stats.fg_made_50_plus || 0,
  fgMade60Plus: (stats: PlayerStats): number => stats.fgMade60Plus || 0,
  extraPointsMade: (stats: PlayerStats): number => stats.extraPointsMade || stats.extra_points_made || 0,
  extraPointAttempts: (stats: PlayerStats): number => stats.extraPointAttempts || stats.extra_points_att || 0,
  puntYards: (stats: PlayerStats): number => stats.puntYards || stats.punt_yards || 0,
  puntsInside20: (stats: PlayerStats): number => stats.puntsInside20 || stats.punts_inside_20 || 0,
  gameWinningKicks: (stats: PlayerStats): number => stats.gameWinningKicks || 0,
  clutchKicks: (stats: PlayerStats): number => stats.clutchKicks || 0,
  fairCatches: (stats: PlayerStats): number => stats.fairCatches || 0,
  touchbacks: (stats: PlayerStats): number => stats.touchbacks || 0,
  puntReturnTds: (stats: PlayerStats): number => stats.puntReturnTds || 0,
  punts: (stats: PlayerStats): number => stats.punts || 0,
};

// Safe accessors for ScoringSystem
export const getScoringField = {
  fgUnder40: (scoring: ScoringSystem): number => scoring.fg_under_40 ?? 3,
  fg4049: (scoring: ScoringSystem): number => scoring.fg_40_49 ?? 4,
  fg50Plus: (scoring: ScoringSystem): number => scoring.fg_50_plus ?? 5,
  fg60Plus: (scoring: ScoringSystem): number => scoring.fg_60_plus ?? 6,
  extraPoint: (scoring: ScoringSystem): number => scoring.extra_point ?? 1,
  fgMiss: (scoring: ScoringSystem): number => scoring.fg_miss ?? -1,
  gameWinnerBonus: (scoring: ScoringSystem): number => scoring.game_winner_bonus ?? 2,
  clutchKickBonus: (scoring: ScoringSystem): number => scoring.clutch_kick_bonus ?? 1,
  puntInside20: (scoring: ScoringSystem): number => scoring.punt_inside_20 ?? 2,
  punt50Plus: (scoring: ScoringSystem): number => scoring.punt_50_plus ?? 1,
  punt60Plus: (scoring: ScoringSystem): number => scoring.punt_60_plus ?? 2,
  touchbackPenalty: (scoring: ScoringSystem): number => scoring.touchback_penalty ?? -1,
  puntReturnTdPenalty: (scoring: ScoringSystem): number => scoring.punt_return_td_penalty ?? -3,
  fairCatchBonus: (scoring: ScoringSystem): number => scoring.fair_catch_bonus ?? 0.5,
};

// Helper to create mock data with both field formats
export const createMockLeague = (data: Partial<League>): League => ({
  id: 1,
  name: 'Mock League',
  league_type: 'season_long',
  max_teams: 10,
  scoring_system_id: 1,
  commissioner_id: 1,
  season_year: 2025,
  is_public: false,
  entry_fee: 0,
  league_status: 'setup',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  waivers_enabled: false,

  // Legacy fields
  leagueType: 'season_long',
  maxTeams: 10,
  scoringSystemId: 1,
  commissionerId: 1,
  seasonYear: 2025,
  isPublic: false,
  entryFee: 0,
  leagueStatus: 'setup',
  createdAt: new Date().toISOString(),
  
  ...data,
});

export const createMockTeam = (data: Partial<Team>): Team => ({
  id: 1,
  league_id: 1,
  user_id: 1,
  team_name: 'Mock Team',
  wins: 0,
  losses: 0,
  points_for: 0,
  points_against: 0,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  
  // Legacy fields
  leagueId: 1,
  userId: 1,
  teamName: 'Mock Team',
  pointsFor: 0,
  pointsAgainst: 0,
  createdAt: new Date().toISOString(),
  
  ...data,
});

export const createMockPlayer = (data: Partial<Player>): Player => ({
  id: 1,
  name: 'Mock Player',
  position: 'K',
  nfl_team_id: 1,
  jersey_number: 1,
  is_active: true,
  experience: 1,
  fantasy_points_avg: 0,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  
  // Legacy fields
  nflTeamId: 1,
  jerseyNumber: 1,
  isActive: true,
  experienceYears: 1,
  
  ...data,
});

export const createMockNFLTeam = (data: any = {}) => ({
  id: 1,
  name: 'Team',
  city: 'City',
  abbreviation: 'TM',
  conference: 'AFC',
  division: 'North',
  stadium_type: 'outdoor' as const,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  
  // Legacy field
  stadiumType: 'outdoor' as const,
  
  ...data,
});

// Safe date formatter that handles undefined
export const formatDateSafe = (dateStr: string | undefined): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

export const formatDateTimeSafe = (dateStr: string | undefined): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
};